import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AdminAuthProvider, useAdminAuth } from "./context/AdminAuthContext";
import AdminLayout from "./components/AdminLayout";
import AdminLogin from "./components/AdminLogin";
import AdminSkeletonLoader from "./components/AdminSkeletonLoader";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded Views for ultra-fast sub-second navigation & memory efficiency
const OverviewView = lazy(() => import("./views/OverviewView"));
const AppsManagerView = lazy(() => import("./views/AppsManagerView"));
const AppsVersionView = lazy(() => import("./views/AppsVersionView"));
const UsersDirectoryView = lazy(() => import("./views/UsersDirectoryView"));
const CreditsManagerView = lazy(() => import("./views/CreditsManagerView"));
const VipManagerView = lazy(() => import("./views/VipManagerView"));
const TransactionsView = lazy(() => import("./views/TransactionsView"));
const SystemSettingsView = lazy(() => import("./views/SystemSettingsView"));
const KeysManagerView = lazy(() => import("./views/KeysManagerView"));
const UpdateRequestsView = lazy(() => import("./views/UpdateRequestsView"));
const EventManagerView = lazy(() => import("./views/EventManagerView"));
const SeasonRewardsView = lazy(() => import("./views/SeasonRewardsView"));
const FaqManagerView = lazy(() => import("./views/FaqManagerView"));
const AuditMonitorView = lazy(() => import("./views/AuditMonitorView"));
const DialogManagerView = lazy(() => import("./views/DialogManagerView"));

function AdminContent() {
  const { isAuthenticated, loading } = useAdminAuth();

  if (loading) {
    return (
      <div className="relative min-h-[100svh] w-full overflow-hidden bg-[#030305] text-foreground flex flex-col items-center justify-center p-6 font-sans">
        {/* Pure Animated Sci-Fi Laser Grid Bar Loader */}
        <div className="w-64 sm:w-80 h-3 bg-black/90 border border-white/20 rounded-md p-0.5 relative overflow-hidden scifi-grid-track shadow-lg">
          <div className="w-1/2 h-full bg-gradient-to-r from-transparent via-primary to-cyan-400 rounded-sm scifi-laser-beam shadow-[0_0_12px_#37B7FF]" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin />;
  }

  return (
    <AdminLayout>
      <ErrorBoundary>
        <Suspense fallback={<AdminSkeletonLoader />}>
          <Routes>
            <Route path="/" element={<OverviewView />} />
            <Route path="/dialog" element={<DialogManagerView />} />
            <Route path="/dialog-manager" element={<DialogManagerView />} />
            <Route path="/apps" element={<AppsManagerView />} />
            <Route path="/versions" element={<AppsVersionView />} />
            <Route path="/users" element={<UsersDirectoryView />} />
            <Route path="/credits" element={<CreditsManagerView />} />
            <Route path="/vip" element={<VipManagerView />} />
            <Route path="/transactions" element={<TransactionsView />} />
            <Route path="/system" element={<SystemSettingsView />} />
            <Route path="/keys" element={<KeysManagerView />} />
            <Route path="/updates" element={<UpdateRequestsView />} />
            <Route path="/events" element={<EventManagerView />} />
            <Route path="/seasons" element={<SeasonRewardsView />} />
            <Route path="/faq" element={<FaqManagerView />} />
            <Route path="/audit" element={<AuditMonitorView />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </AdminLayout>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <AdminContent />
      </AdminAuthProvider>
    </BrowserRouter>
  );
}
